import type { TCFAPI } from '@mbrtargeting/metatag-cmp-types';
import { isBoolean, isFunction, isString } from '@mbrtargeting/metatag-utils';
import type { ObjectWithTcfApiCall, ObjectWithTcfApiReturn } from './interfaces.js';
import { extractData } from './utils/extract-data.js';
import { addLocatorFrame, lookupCmpFrame } from './utils/frame-helper.js';

declare global {
  interface Window extends TCFAPI {
    __tcfapiLocator?: Window;
    Cypress?: unknown;
  }
}

export interface CmpStubOptions {
  win?: Window;
  traverse?: boolean;
}

export const createCmpStub = ({ win = window, traverse = !window.Cypress }: CmpStubOptions = {}): void => {

  const queue: any = [];
  let gdprApplies: boolean = true;

  /**
   * The stubbed __tcfapi method will be used to queue calls until the full CMP implementation loads.
   *
   * The calls with command 'setGdprApplies' or 'ping' will be handled and answered directly by the stub.
   * All other commands are put into a queue in incoming order and processed later.
   */
  const tcfapiStubHandler = (...args: any[]): void => {
    if (!args.length) {
      // shortcut to get the queue when the full CMP implementation loads; it can call tcfapiHandler() with no arguments to get the queued arguments
      return queue;
    } else if (args[0] === 'setGdprApplies') {
      // shortcut to set gdprApplies if the publisher knows that they apply GDPR rules to all traffic
      if (args.length > 3 && parseInt(args[1], 10) === 2 && isBoolean(args[3])) {
        gdprApplies = args[3];
        if (isFunction(args[2])) {
          args[2]('set', true);
        }
      }
    } else if (args[0] === 'ping') {
      // Only supported method; give PingReturn object as response
      const retr = {
        gdprApplies,
        cmpLoaded: false,
        cmpStatus: 'stub',
      };
      if (isFunction(args[2])) {
        args[2](retr);
      }
    } else {
      // some other method, just queue it for the full CMP implementation to deal with
      queue.push(args);
    }
  }

  /**
   * This postMessage handler is installed in the same window as the stubbed __tcfapi method.
   * It will listen for __tcfapiCall messages from iframes and ask the __tcfapi method (stubbed or later full implementation) to handle them.
   * The __tcfapi response will be sent back to the iframe via __tcfapiReturn message.
   */
  const postMessageStubHandler = (event: MessageEvent<string | ObjectWithTcfApiCall>) => {
    const needsStringify = isString(event.data);
    const payload = extractData<ObjectWithTcfApiCall>(event.data)?.__tcfapiCall;

    if (payload) {
      window.__tcfapi!(
        payload.command,
        payload.version,
        (returnValue: unknown, success: boolean) => {
          const msg: ObjectWithTcfApiReturn = {
            __tcfapiReturn: {
              returnValue,
              success,
              callId: payload.callId,
            },
          };
          (event.source as Window).postMessage(needsStringify ? JSON.stringify(msg) : msg, '*');
        },
        payload.parameter,
      );
    }
  }

  const cmpFrame = traverse ? lookupCmpFrame(win) : undefined;

  if (!cmpFrame) {
    // we have recur'd up the windows and have found no __tcfapiLocator frame
    addLocatorFrame(win);
    win.__tcfapi = tcfapiStubHandler;
    win.addEventListener('message', postMessageStubHandler, false);
  }
}
